import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, from, of, throwError, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { resolveUploadContentType } from '../utils/file-upload/resolve-upload-content-type';

export type S3VerifyStatus = 'exists' | 'missing' | 'error';

export interface S3UploadVerifyConfig {
  endpoint?: string;
  withAuth?: boolean;
}

export const S3_UPLOAD_VERIFY_FAILED_MESSAGE =
  'Não foi possível confirmar o envio do arquivo. Tente novamente.';

export const S3_UPLOAD_EMPTY_FILE_MESSAGE =
  'O arquivo chegou vazio (0 bytes). Selecione novamente e tente de novo.';

const VERIFY_MAX_ATTEMPTS = 3;

@Injectable({
  providedIn: 'root',
})
export class S3UploadService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private platformId = inject(PLATFORM_ID);
  private baseUrl = environment.apiUrl;

  /**
   * PUT binário direto no S3 via `fetch` nativo.
   *
   * Sempre materializa os bytes (`arrayBuffer` → `Blob`) imediatamente antes do
   * PUT. Não confiar em `File.size` nem em `new File([file])` — no Safari/iOS
   * isso gera objeto com `Content-Type` ok e `Content-Length: 0`.
   */
  uploadToS3(presignedUrl: string, file: File): Observable<{ success: true }> {
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => new Error('Upload S3 disponível apenas no browser.'));
    }

    return from(this.putMaterializedBody(presignedUrl, file));
  }

  private async putMaterializedBody(
    presignedUrl: string,
    file: File
  ): Promise<{ success: true }> {
    const contentType = resolveUploadContentType(file);

    let buffer: ArrayBuffer;
    try {
      buffer = await file.arrayBuffer();
    } catch {
      throw new Error(S3_UPLOAD_EMPTY_FILE_MESSAGE);
    }

    if (buffer.byteLength === 0) {
      throw new Error(S3_UPLOAD_EMPTY_FILE_MESSAGE);
    }

    // Blob a partir do buffer (não `File` wrap) — corpo confiável no WebKit.
    const body = new Blob([buffer], { type: contentType });

    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body,
    });

    if (!response.ok) {
      let detail = '';
      try {
        detail = (await response.text()).trim().slice(0, 200);
      } catch {
        /* ignore */
      }
      throw new Error(
        detail
          ? `Falha no upload S3 (${response.status}): ${detail}`
          : `Falha no upload S3 (${response.status}).`
      );
    }

    return { success: true };
  }

  verifyObjectExists(objectKey: string, config?: S3UploadVerifyConfig): Observable<S3VerifyStatus> {
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => new Error('Verificação S3 disponível apenas no browser.'));
    }

    const endpoint = config?.endpoint ?? `${this.baseUrl}/upload/verify`;
    const withAuth = config?.withAuth !== false;

    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (withAuth) {
      headers = headers.set('Authorization', `Bearer ${this.authService.getToken()}`);
    }

    return this.http
      .post<{ status: S3VerifyStatus; objectKey: string }>(endpoint, { objectKey }, { headers })
      .pipe(map((response) => response.status));
  }

  uploadWithVerification(
    presignedUrl: string,
    file: File,
    objectKey: string,
    config?: S3UploadVerifyConfig
  ): Observable<{ success: true }> {
    return this.uploadToS3(presignedUrl, file).pipe(
      switchMap(() => this.verifyObjectExistsWithRetry(objectKey, config)),
      switchMap((status) => {
        if (status === 'exists') {
          return of({ success: true as const });
        }
        return throwError(() => new Error(S3_UPLOAD_VERIFY_FAILED_MESSAGE));
      })
    );
  }

  private verifyObjectExistsWithRetry(
    objectKey: string,
    config?: S3UploadVerifyConfig,
    attempt = 1
  ): Observable<S3VerifyStatus> {
    return this.verifyObjectExists(objectKey, config).pipe(
      switchMap((status) => {
        if (status === 'exists' || attempt >= VERIFY_MAX_ATTEMPTS) {
          return of(status);
        }

        const delayMs = 300 + Math.floor(Math.random() * 500);
        return timer(delayMs).pipe(
          switchMap(() => this.verifyObjectExistsWithRetry(objectKey, config, attempt + 1))
        );
      })
    );
  }
}
